import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";
import { BrutalCard, BrutalButton } from "../components/Brutal";
import { Tag, Warning, Copy } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const CATEGORY_ORDER = ["Injectable", "Precision Nasal Mist", "Flagship Topical"];

const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(0)}`);

function marginOf(p) {
  if (p.wholesale_price == null || p.landed_cost == null) return null;
  return p.wholesale_price - p.landed_cost;
}

export default function PricingAuditPage() {
  const { t } = useTranslation();
  const { canSeeCost, isOwner, isSuperOwner } = useAuth();
  const [products, setProducts] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/products`, { withCredentials: true })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]));
  }, []);

  const groups = useMemo(() => {
    const by = {};
    (products || []).forEach((p) => {
      (by[p.category || "—"] = by[p.category || "—"] || []).push(p);
    });
    Object.values(by).forEach((list) => list.sort((a, b) => a.name.localeCompare(b.name)));
    const keys = Object.keys(by).sort(
      (a, b) => (CATEGORY_ORDER.indexOf(a) + 99) - (CATEGORY_ORDER.indexOf(b) + 99)
    );
    return keys.map((k) => [k, by[k]]);
  }, [products]);

  if (!(isOwner || isSuperOwner) || !canSeeCost) return <Navigate to="/" replace />;
  if (products === null) return <div className="p-8 text-sm text-zinc-500">…</div>;

  const anomalies = products.filter(
    (p) => p.landed_cost == null || p.wholesale_price == null ||
      p.suggested_retail_price == null || (marginOf(p) ?? 1) <= 0
  );

  // includeCost=false -> distributor-safe list (WS + SRP only, no costs)
  const copyList = async (includeCost) => {
    const lines = [];
    groups.forEach(([cat, list]) => {
      lines.push(`${cat.toUpperCase()} (${list.length})`);
      list.forEach((p) => {
        const parts = [];
        if (includeCost && p.landed_cost != null) parts.push(`Cost ${money(p.landed_cost)}`);
        parts.push(`WS ${money(p.wholesale_price)}`);
        parts.push(`SRP ${money(p.suggested_retail_price)}`);
        lines.push(`${p.name} — ${parts.join(" · ")}`);
      });
      lines.push("");
    });
    try {
      await navigator.clipboard.writeText(lines.join("\n").trim());
      toast.success(t(includeCost ? "pricing_audit.copied" : "pricing_audit.copied_ws"));
    } catch {
      toast.error(t("pricing_audit.copy_failed"));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="pricing-audit-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Tag size={22} weight="bold" className="text-[#8a6a1c]" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">{t("pricing_audit.title")}</h1>
            <p className="text-sm text-zinc-500">
              {t("pricing_audit.subtitle", { count: products.length })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <BrutalButton
            size="sm"
            data-testid="copy-price-list-btn"
            onClick={() => copyList(true)}
          >
            <Copy size={14} weight="bold" /> {t("pricing_audit.copy_list")}
          </BrutalButton>
          <BrutalButton
            size="sm"
            variant="secondary"
            data-testid="copy-ws-list-btn"
            onClick={() => copyList(false)}
          >
            <Copy size={14} weight="bold" /> {t("pricing_audit.copy_ws_list")}
          </BrutalButton>
        </div>
      </div>

      {anomalies.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5" data-testid="pricing-audit-anomalies">
          <Warning size={16} weight="bold" />
          {t("pricing_audit.anomalies", { count: anomalies.length })}
        </div>
      )}

      {groups.map(([category, list]) => (
        <BrutalCard key={category} className="p-0 overflow-hidden" data-testid={`pricing-audit-group-${category}`}>
          <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50/60 flex items-baseline justify-between">
            <h2 className="text-sm font-semibold tracking-wide uppercase text-zinc-700">{category}</h2>
            <span className="text-xs font-mono text-zinc-400">{list.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                  <th className="px-5 py-2 font-medium">{t("pricing_audit.product")}</th>
                  <th className="px-3 py-2 font-medium text-right">{t("pricing_audit.cost")}</th>
                  <th className="px-3 py-2 font-medium text-right">{t("pricing_audit.wholesale")}</th>
                  <th className="px-3 py-2 font-medium text-right">{t("pricing_audit.srp")}</th>
                  <th className="px-3 py-2 font-medium text-right">{t("pricing_audit.margin")}</th>
                  <th className="px-3 py-2 font-medium text-right">{t("pricing_audit.margin_pct")}</th>
                  <th className="px-5 py-2 font-medium text-right">{t("pricing_audit.retail_spread")}</th>
                </tr>
              </thead>
              <tbody>
                {list.map((p) => {
                  const m = marginOf(p);
                  const pct = m != null && p.wholesale_price ? (m / p.wholesale_price) * 100 : null;
                  const spread = p.suggested_retail_price != null && p.wholesale_price != null
                    ? p.suggested_retail_price - p.wholesale_price : null;
                  const bad = m != null && m <= 0;
                  const missing = p.landed_cost == null || p.wholesale_price == null || p.suggested_retail_price == null;
                  return (
                    <tr
                      key={p.id}
                      className={`border-b border-zinc-50 last:border-0 ${bad ? "bg-red-50" : missing ? "bg-amber-50" : "hover:bg-zinc-50/50"}`}
                      data-testid={`pricing-row-${p.id}`}
                    >
                      <td className="px-5 py-2 font-medium text-zinc-900">{p.name}</td>
                      <td className="px-3 py-2 text-right font-mono">{money(p.landed_cost)}</td>
                      <td className="px-3 py-2 text-right font-mono">{money(p.wholesale_price)}</td>
                      <td className="px-3 py-2 text-right font-mono">{money(p.suggested_retail_price)}</td>
                      <td className={`px-3 py-2 text-right font-mono font-semibold ${bad ? "text-red-700" : "text-[#8a6a1c]"}`}>
                        {m == null ? "—" : money(m)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-zinc-600">
                        {pct == null ? "—" : `${pct.toFixed(0)}%`}
                      </td>
                      <td className="px-5 py-2 text-right font-mono text-zinc-600">{money(spread)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </BrutalCard>
      ))}
    </div>
  );
}
